/**
 * Shared formatting helpers.
 *
 * Mirrors american-drone-network-web/src/app/shared/utils/format.utils.ts —
 * keep the two in sync.
 */

/**
 * Format a US phone number for display.
 * Converts +18337371332 to (833) 737-1332
 *
 * @param phone - Phone number in any format (with or without country code)
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  // Handle US numbers with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const areaCode = cleaned.substring(1, 4);
    const exchange = cleaned.substring(4, 7);
    const subscriber = cleaned.substring(7, 11);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  // Handle US numbers without country code (10 digits)
  if (cleaned.length === 10) {
    const areaCode = cleaned.substring(0, 3);
    const exchange = cleaned.substring(3, 6);
    const subscriber = cleaned.substring(6, 10);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  // Return original if format not recognized
  return phone;
}

/**
 * Reduce a stored phone number to its 10 national digits, ready to hand to a
 * `(000) 000-0000` input mask.
 *
 * Stored values are not uniform — the Hub notes they "may be stored in various
 * formats (e.g., +1-555-123-4567)". Feeding an 11-digit value straight into a
 * 10-digit mask drops the *last* digit, so "+1-555-123-4567" would silently
 * become "(155) 512-3456" and save back corrupted. Dropping the country code
 * first keeps the number intact.
 *
 * Anything that isn't 11 digits starting with 1 is returned as plain digits and
 * left for the mask to handle.
 */
export function toNationalDigits(phone: string | null | undefined): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.substring(1);
  }
  return cleaned;
}
